<script setup>
/**
 * One key, as it can be read back — which is to say, without its secret.
 *
 * THE PLAINTEXT IS NOT A FIELD OF THE OBJECT THIS COMPONENT IS HANDED, and that is a
 * structural guarantee rather than a promise: `readOutboundKeyRow` in `api/client.js`
 * builds the row field by field from a fixed list that does not include `secret`, and the
 * store behind it holds a one-way sha256, so no read path can produce one. The mint's
 * answer is the only object in this section that carries a credential and it goes to
 * `OutboundSecretPanel` and nowhere else — never to a row, a toast or a log line.
 *
 * WHAT THE ROW SAYS, and why each part earns its space: the name the owner gave it (so
 * she knows which of her systems this is), the number it may send from (a key pinned to
 * the wrong sender is a working integration that breaks on someone else's change), when
 * it was made, and when it was last used. That last one is the field that answers "is
 * anything still using this?" — which is the question you ask right before turning a key
 * off, and the reason the revoke button is on the same row rather than behind a menu.
 *
 * AND, SINCE W-03, WHETHER ANY OF THAT USE ACTUALLY WENT THROUGH. "Last used" on its own
 * was a trap: it is stamped BEFORE the send is judged, so a key refused on every call read
 * as the liveliest row in the list. See `refusal` below.
 *
 * A TURNED-OFF KEY IS STILL LISTED. It is history that explains a system that stopped
 * working, and hiding it would leave an owner with a silent integration and no record of
 * why. It carries a badge, and it offers no button — there is nothing left to do to it.
 */
import { computed } from 'vue';

import Button from 'dashboard/components-next/button/Button.vue';

import { useWasloI18n } from '../../i18n.js';
import { relativeTime } from '../../helpers/relative-time.js';

const props = defineProps({
  /** One row from `wasloApi.getOutboundKeys`. Never the mint's answer. */
  keyRow: { type: Object, required: true },
  /**
   * What to call the number this key sends from — resolved by the page, which is the only
   * place that has both the sender list and the workspace's own inbox names. `null` means
   * the key is pinned to a number this workspace can no longer see; `''` means it is
   * pinned to nothing, which is a real and ordinary answer.
   */
  senderLabel: { type: String, default: '' },
  /** True while THIS key's revoke is in flight. */
  isRevoking: { type: Boolean, default: false },
});

const emit = defineEmits(['revoke']);

const { t, locale } = useWasloI18n();

const when = iso =>
  relativeTime(iso, locale.value, {
    justNow: t('WASLO.OUTBOUND.COMMON.JUST_NOW'),
  }) ?? t('WASLO.OUTBOUND.COMMON.UNKNOWN');

const isRevoked = computed(() => props.keyRow.revokedAt !== null);

const createdLine = computed(() =>
  t('WASLO.OUTBOUND.KEYS.ROW.CREATED', { when: when(props.keyRow.createdAt) })
);

/**
 * "Never used" is not the same claim as "used, we don't know when", so a null is rendered
 * as its own sentence rather than passed through the `{when}` template.
 */
const lastUsedLine = computed(() =>
  props.keyRow.lastUsedAt === null
    ? t('WASLO.OUTBOUND.KEYS.ROW.NEVER_USED')
    : t('WASLO.OUTBOUND.KEYS.ROW.LAST_USED', {
        when: when(props.keyRow.lastUsedAt),
      })
);

const revokedLine = computed(() =>
  props.keyRow.revokedAt === null
    ? ''
    : t('WASLO.OUTBOUND.KEYS.ROW.REVOKED', {
        when: when(props.keyRow.revokedAt),
      })
);

/**
 * ── WHY A KEY THAT IS BEING REFUSED HAS TO SAY SO ──────────────────────────────────
 *
 * "Last used {when}" was this row's only sign of life, and it is stamped BEFORE the send
 * is judged — so a website whose every call was rejected read "Last used just now" while
 * not one message had gone out. The healthiest-looking row in the list was the broken
 * one, and the owner's first symptom was a customer asking why nobody confirmed her order.
 *
 * THE CODE IS TURNED INTO A SENTENCE HERE, never printed. The server emits conditions and
 * this file emits words, which is the only way the line is in the owner's language (the
 * rule `vocabulary.spec.js` exists to hold). A code we have never seen still gets a
 * sentence — with the count and the time, which are true whatever the reason was.
 */
const REFUSAL_COPY = {
  sender_removed: 'SENDER_REMOVED',
  outbound_not_provisioned: 'NOT_SET_UP',
  ambiguous_sender: 'AMBIGUOUS_SENDER',
  rate_limited: 'RATE_LIMITED',
  monthly_allowance_exhausted: 'MONTHLY_ALLOWANCE',
  outbound_disabled: 'DISABLED',
  outbound_paused: 'PAUSED',
  plan_required: 'PLAN_REQUIRED',
  invalid_request: 'INVALID_REQUEST',
  country_not_allowed: 'COUNTRY_NOT_ALLOWED',
  circuit_open: 'CIRCUIT_OPEN',
  busy: 'BUSY',
  idempotency_key_conflict: 'IDEMPOTENCY_CONFLICT',
};

/**
 * The whole `template_*` family says one thing to an owner — the message it tried to send
 * is not one she may send — and which of them it was is a distinction only we can act on.
 */
const TEMPLATE_PREFIX = 'template_';

/**
 * A MONTH, and then the whole line goes — count and all. A refusal from last spring
 * explains nothing about today's integration, and a warning that is always on is a
 * warning nobody reads.
 *
 * THE COUNT IS A LIFETIME TOTAL, and this comment claimed the opposite until W-03's
 * review: it said the number was the count behind that last refusal. It is not. The
 * sidecar's `recordRefusal` does `refusal_count + 1` and NOTHING ever resets it, so a key
 * refused 400 times in January and once yesterday carries 401. Only `lastRefusedAt` is
 * recent — which is why the copy says "in total" (`REFUSED_MANY`): the two halves of that
 * sentence are measured over different spans, and "Refused 401 times · last yesterday"
 * reads as 401 refusals yesterday.
 *
 * So this window governs whether the LINE appears at all. It has never scoped the number,
 * and a reader who believed otherwise would have read every count on this screen wrong.
 */
const REFUSAL_WINDOW_MS = 30 * 24 * 60 * 60 * 1000;

const refusal = computed(() => {
  const { refusalCount, lastRefusedAt } = props.keyRow;
  if (!(refusalCount > 0) || lastRefusedAt === null) return '';

  const at = new Date(lastRefusedAt);
  if (Number.isNaN(at.getTime())) return '';
  if (Date.now() - at.getTime() > REFUSAL_WINDOW_MS) return '';

  const code = props.keyRow.lastRefusalCode ?? '';
  // `typeof`, not truthiness: `REFUSAL_COPY.constructor` is a FUNCTION, and a refusal
  // code of `constructor` would otherwise be handed to `t()` as a translation key.
  const named = REFUSAL_COPY[code];
  let key = 'UNKNOWN';
  if (code.startsWith(TEMPLATE_PREFIX)) key = 'TEMPLATE';
  else if (typeof named === 'string') key = named;
  const values = {
    count: refusalCount,
    when: when(lastRefusedAt),
    reason: t(`WASLO.OUTBOUND.KEYS.REFUSAL.${key}`),
  };
  return refusalCount === 1
    ? t('WASLO.OUTBOUND.KEYS.ROW.REFUSED_ONE', values)
    : t('WASLO.OUTBOUND.KEYS.ROW.REFUSED_MANY', values);
});

const senderLine = computed(() => {
  if (props.keyRow.inboxId === null) {
    return t('WASLO.OUTBOUND.KEYS.ROW.SENDER_ANY');
  }
  return t('WASLO.OUTBOUND.KEYS.ROW.SENDER', {
    number: props.senderLabel || t('WASLO.OUTBOUND.COMMON.SENDER_UNKNOWN'),
  });
});
</script>

<template>
  <li
    class="flex flex-col gap-3 p-4 outline outline-1 -outline-offset-1 outline-n-weak rounded-xl bg-n-solid-1"
    :class="isRevoked ? 'opacity-70' : ''"
    :data-waslo-outbound-key="keyRow.id"
    :data-waslo-outbound-key-revoked="isRevoked"
  >
    <div class="flex flex-wrap items-start justify-between gap-3">
      <div class="flex flex-col min-w-0 gap-1">
        <!--
          The owner's own words, so it goes through an isolate: a label written in Arabic
          beside an ASCII prefix reorders without one. `break-words` because nothing stops
          her naming it after a 60-character URL.
        -->
        <span
          class="min-w-0 text-sm font-medium break-words text-n-slate-12"
          data-waslo-outbound-key-label
        >
          <bdi>{{ keyRow.label || t('WASLO.OUTBOUND.KEYS.ROW.NO_LABEL') }}</bdi>
        </span>
        <span class="min-w-0 text-sm break-all text-n-slate-11">
          {{ t('WASLO.OUTBOUND.KEYS.ROW.PREFIX') }}
          <bdi>{{ keyRow.keyPrefix }}</bdi>
        </span>
      </div>

      <span
        v-if="isRevoked"
        class="inline-flex items-center h-6 px-2 text-xs rounded-md shrink-0 bg-n-slate-3 text-n-slate-11"
        data-waslo-outbound-key-badge
      >
        {{ t('WASLO.OUTBOUND.KEYS.ROW.REVOKED_BADGE') }}
      </span>
    </div>

    <!-- Chips wrap and the row carries `min-w-0`: README §1, measured at panel width. -->
    <div
      class="flex flex-wrap items-center min-w-0 gap-x-3 gap-y-1 text-xs text-n-slate-10"
    >
      <span data-waslo-outbound-key-sender>{{ senderLine }}</span>
      <span aria-hidden="true">·</span>
      <span>{{ createdLine }}</span>
      <span aria-hidden="true">·</span>
      <span data-waslo-outbound-key-last-used>{{ lastUsedLine }}</span>
      <template v-if="isRevoked">
        <span aria-hidden="true">·</span>
        <span>{{ revokedLine }}</span>
      </template>
    </div>

    <!--
      A LINE OF ITS OWN, not a fourth chip in the row above: it is the one fact here that
      contradicts the rest of the row, and a refusal wrapped in among "created" and "last
      used" reads as more of the same bookkeeping. Ruby, because this is what is wrong.
    -->
    <p
      v-if="refusal"
      class="mb-0 text-xs break-words text-n-ruby-11"
      data-waslo-outbound-key-refusals
    >
      {{ refusal }}
    </p>

    <div v-if="!isRevoked" class="flex">
      <Button
        :label="t('WASLO.OUTBOUND.KEYS.REVOKE.ACTION')"
        variant="faded"
        color="ruby"
        size="sm"
        icon="i-lucide-power-off"
        :is-loading="isRevoking"
        :disabled="isRevoking"
        data-waslo-outbound-key-revoke
        @click="emit('revoke', keyRow)"
      />
    </div>
  </li>
</template>
